import { useCallback, useEffect, useMemo, useState } from "react";
import { Fingerprint, RefreshCw, Router, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { atLeast } from "@/lib/roles";
import { todayLocal } from "@/lib/periods";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DatePicker } from "@/components/DatePicker";
import { getGateEntries, getGateDevices, updateGateDevice, resyncGateDevice, getGateUsers, linkGateUser } from "@/lib/api";

// Entries from the fingerprint reader at the gym door (eSSL / ZKTeco pushing to /iclock).

const STATUS = {
  active: { label: "Active", cls: "text-emerald-300 bg-emerald-500/10 border-emerald-500/30" },
  expired: { label: "Expired", cls: "text-rose-300 bg-rose-500/10 border-rose-500/30" },
  not_started: { label: "Not started", cls: "text-amber-300 bg-amber-500/10 border-amber-500/30" },
  unknown: { label: "No expiry on file", cls: "text-zinc-300 bg-zinc-500/10 border-zinc-500/30" },
  unlinked: { label: "Not linked", cls: "text-zinc-400 bg-zinc-500/10 border-zinc-600/40" },
};
const Chip = ({ s }) => {
  const x = STATUS[s] || STATUS.unlinked;
  return <span className={`inline-flex rounded-full border px-2 py-0.5 text-xs ${x.cls}`}>{x.label}</span>;
};
const ago = (iso) => {
  if (!iso) return "never";
  const m = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  return m < 1 ? "just now" : m < 60 ? `${m} min ago` : m < 1440 ? `${Math.round(m / 60)} h ago` : `${Math.round(m / 1440)} d ago`;
};
const dmy = (iso) => (iso ? iso.slice(0, 10).split("-").reverse().join("-") : "");

function Readers({ isMaster, isAdmin, facilities, activeId }) {
  const [devices, setDevices] = useState(null);
  const [pick, setPick] = useState({});
  const load = useCallback(() => getGateDevices().then((d) => setDevices(d.devices)).catch(() => setDevices([])), []);
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, [load]);
  if (!devices) return null;
  const mine = devices.filter((d) => d.approved && d.facility_id === activeId);
  const pending = devices.filter((d) => !d.approved);

  const approve = async (d) => {
    const facility_id = pick[d.sn] || activeId;
    try {
      await updateGateDevice(d.sn, { facility_id, approved: true, name: d.name || "Front gate" });
      toast.success("Reader approved. Its users and entries will arrive within a minute or two.");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't approve the reader"); }
  };

  return (
    <section className="card-surface p-5 space-y-3" data-testid="gate-readers">
      <div className="flex items-center gap-2"><Router className="h-4 w-4 text-zinc-400" /><h3 className="font-semibold">Readers</h3></div>
      {mine.length === 0 && pending.length === 0 && (
        <p className="text-sm text-zinc-500">
          No reader connected to this gym yet. On the reader: Menu → Comm. → Cloud Server Setting → server address
          <b className="text-zinc-300"> app.liftr.info</b>, port 443, HTTPS on. It will then appear here for a Master Admin to approve.
        </p>
      )}
      {mine.map((d) => (
        <div key={d.sn} className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
          <span className={`h-2 w-2 rounded-full ${d.online ? "bg-emerald-400" : "bg-rose-400"}`} />
          <span className="font-medium">{d.name || "Reader"}</span>
          <span className="text-zinc-500 font-mono text-xs">{d.sn}</span>
          <span className="text-zinc-500 text-xs">{d.online ? "online" : "offline"} · last seen {ago(d.last_seen)} · {d.punches_received || 0} entries received</span>
          {isAdmin && (
            <Button size="sm" variant="ghost" className="h-7 text-xs"
              onClick={() => resyncGateDevice(d.sn).then(() => toast.success("Asked the reader to re-send its users and entries"))}>
              <RefreshCw className="h-3.5 w-3.5 mr-1" />Re-sync
            </Button>
          )}
        </div>
      ))}
      {isMaster && pending.map((d) => (
        <div key={d.sn} className="flex flex-wrap items-center gap-2 text-sm rounded-md border border-amber-500/30 bg-amber-500/5 p-3">
          <span className="text-amber-300">New reader waiting</span>
          <span className="font-mono text-xs text-zinc-400">{d.sn}</span>
          <span className="text-xs text-zinc-500">from {d.last_ip || "?"} · {ago(d.last_seen)}{d.refused_lines ? ` · holding ${d.refused_lines} records` : ""}</span>
          <select className="ml-auto bg-[#0a0a0a] border border-[#262626] rounded-md h-8 px-2 text-sm"
            value={pick[d.sn] || activeId || ""} onChange={(e) => setPick((p) => ({ ...p, [d.sn]: e.target.value }))}
            aria-label="Gym for this reader">
            {facilities.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
          </select>
          <Button size="sm" className="h-8 bg-blue-600 hover:bg-blue-500" onClick={() => approve(d)}>
            <CheckCircle2 className="h-4 w-4 mr-1" />Approve
          </Button>
        </div>
      ))}
    </section>
  );
}

function Enrolled() {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const load = useCallback(() => getGateUsers().then(setData).catch(() => setData({ users: [], members: [] })), []);
  useEffect(() => { load(); }, [load]);
  if (!data) return null;
  if (!data.users.length) return null;
  const users = data.users.filter((u) => !q || `${u.name} ${u.pin} ${u.member?.name || ""}`.toLowerCase().includes(q.toLowerCase()));
  const unlinked = data.users.filter((u) => !u.customer_key).length;

  const link = async (u, customer_key) => {
    try {
      await linkGateUser(u.sn, u.pin, customer_key || null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't link"); }
  };

  return (
    <section className="card-surface p-5 space-y-3" data-testid="gate-users">
      <div className="flex flex-wrap items-center gap-3">
        <Fingerprint className="h-4 w-4 text-zinc-400" />
        <h3 className="font-semibold">Fingerprints on the reader</h3>
        <span className="text-xs text-zinc-500">{data.users.length} enrolled · {unlinked} not linked to a member</span>
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search" className="sm:ml-auto w-full sm:w-48 h-9 sm:h-8 bg-[#0a0a0a] border-[#262626]" />
      </div>
      <p className="text-xs text-zinc-500">Names that match one member exactly are linked automatically. Link the rest so their entries show who walked in.</p>
      {/* Phones: one row per fingerprint, with the member picker full width */}
      <div className="md:hidden space-y-2 max-h-[60vh] overflow-y-auto">
        {users.map((u) => (
          <div key={`${u.sn}-${u.pin}`} className="rounded-lg border border-[#1f1f1f] p-3 space-y-2">
            <div className="flex items-center justify-between gap-2">
              <div className="min-w-0"><span className="font-mono text-xs text-zinc-500 mr-2">#{u.pin}</span>{u.name || <span className="text-zinc-600">no name</span>}</div>
              {u.member ? <Chip s={u.member.status} /> : <Chip s="unlinked" />}
            </div>
            <select className="w-full bg-[#0a0a0a] border border-[#262626] rounded-md h-10 px-2"
              value={u.customer_key || ""} onChange={(e) => link(u, e.target.value)} aria-label={`FPA member for ${u.name || u.pin}`}>
              <option value="">— not linked —</option>
              {data.members.map((m) => <option key={m.customer_key} value={m.customer_key}>{m.name}{m.phone ? ` · ${m.phone}` : ""}</option>)}
            </select>
          </div>
        ))}
      </div>
      <div className="hidden md:block max-h-96 overflow-auto -mx-5 px-5">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[11px] uppercase tracking-wider text-zinc-500 border-b border-[#262626]">
              <th className="text-left font-medium py-2 w-16">ID</th>
              <th className="text-left font-medium py-2">Name on reader</th>
              <th className="text-left font-medium py-2">FPA member</th>
              <th className="text-left font-medium py-2">Status</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={`${u.sn}-${u.pin}`} className="border-b border-[#1a1a1a]">
                <td className="py-2 font-mono text-xs text-zinc-400">{u.pin}</td>
                <td className="py-2">{u.name || <span className="text-zinc-600">no name</span>}</td>
                <td className="py-2">
                  <select className="bg-[#0a0a0a] border border-[#262626] rounded-md h-8 px-2 text-sm max-w-[260px]"
                    value={u.customer_key || ""} onChange={(e) => link(u, e.target.value)} aria-label={`FPA member for ${u.name || u.pin}`}>
                    <option value="">— not linked —</option>
                    {data.members.map((m) => <option key={m.customer_key} value={m.customer_key}>{m.name}{m.phone ? ` · ${m.phone}` : ""}</option>)}
                  </select>
                </td>
                <td className="py-2">{u.member ? <Chip s={u.member.status} /> : <Chip s="unlinked" />}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

export default function Gate() {
  const { activeFacility, effectiveRole, user, facilities } = useAuth();
  const [day, setDay] = useState(() => todayLocal(activeFacility?.timezone));
  const [data, setData] = useState(null);
  const isManager = atLeast(effectiveRole, "manager");
  const isAdmin = atLeast(effectiveRole, "admin");
  const isMaster = user?.role === "master_admin";

  const load = useCallback(() => getGateEntries(day).then(setData).catch(() => setData({ entries: [], counts: {} })), [day]);
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, [load]);

  const people = useMemo(() => (data?.entries || []).filter((e) => !e.repeat), [data]);
  const c = data?.counts || {};

  return (
    <div className="space-y-6" data-testid="gate-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="eyebrow">{activeFacility?.name} · fingerprint reader</div>
          <h2 className="font-display text-3xl font-extrabold mt-1 tracking-tight">Gate entries</h2>
          <p className="text-sm text-zinc-400 mt-1">Who came in through the door, from the fingerprint reader. Updates every 30 seconds.</p>
        </div>
        <div className="w-40"><DatePicker value={day} onChange={(e) => e.target.value && setDay(e.target.value)} data-testid="gate-date" /></div>
      </div>

      {isManager && <Readers isMaster={isMaster} isAdmin={isAdmin} facilities={facilities} activeId={activeFacility?.id} />}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[["People in", c.people], ["Taps", c.taps], ["Not active", c.not_active, c.not_active ? "text-rose-400" : ""], ["Not linked", c.unlinked]].map(([label, v, cls]) => (
          <div key={label} className="card-surface p-5">
            <div className="eyebrow">{label}</div>
            <div className={`font-display text-3xl font-extrabold mt-2 tabular-nums ${cls || ""}`}>{v ?? "–"}</div>
          </div>
        ))}
      </div>

      <section className="card-surface p-5">
        <div className="flex items-baseline justify-between mb-3">
          <h3 className="font-semibold">{dmy(data?.date || day)}</h3>
          <span className="text-xs text-zinc-500">{people.length} people · repeat taps shown dimmed</span>
        </div>
        {!data ? <p className="text-sm text-zinc-500">Loading…</p> : !data.entries.length ? (
          <p className="text-sm text-zinc-500">No entries for this day.</p>
        ) : (
          <>
          {/* Phones: one line per tap */}
          <ul className="md:hidden divide-y divide-[#1a1a1a]" data-testid="gate-entry-list">
            {data.entries.map((e) => (
              <li key={e.id} className={`py-2.5 flex items-center gap-3 ${e.repeat ? "text-zinc-600" : ""}`}>
                <span className="tabular-nums text-sm w-11 shrink-0">{e.at.slice(11, 16)}</span>
                <div className="min-w-0 flex-1">
                  <div className="truncate">{e.name || <span className="text-zinc-500">Unknown</span>}</div>
                  {!e.repeat && e.end_date && <div className="text-[11px] text-zinc-500">expires {dmy(e.end_date)}</div>}
                </div>
                {!e.repeat && <Chip s={e.customer_key ? e.member_status || "unknown" : "unlinked"} />}
              </li>
            ))}
          </ul>
          <div className="hidden md:block overflow-x-auto -mx-5 px-5">
            <table className="w-full text-sm min-w-[520px]" data-testid="gate-entries">
              <thead>
                <tr className="text-[11px] uppercase tracking-wider text-zinc-500 border-b border-[#262626]">
                  <th className="text-left font-medium py-2 w-20">Time</th>
                  <th className="text-left font-medium py-2">Name</th>
                  <th className="text-left font-medium py-2">Membership</th>
                  <th className="text-left font-medium py-2">Expires</th>
                  <th className="text-left font-medium py-2 w-16">ID</th>
                </tr>
              </thead>
              <tbody>
                {data.entries.map((e) => (
                  <tr key={e.id} className={`border-b border-[#1a1a1a] ${e.repeat ? "text-zinc-600" : ""}`}>
                    <td className="py-2 tabular-nums">{e.at.slice(11, 16)}</td>
                    <td className="py-2">{e.name || <span className="text-zinc-500">Unknown</span>}</td>
                    <td className="py-2">{e.repeat ? null : <Chip s={e.customer_key ? e.member_status || "unknown" : "unlinked"} />}</td>
                    <td className="py-2 tabular-nums">{e.repeat ? null : dmy(e.end_date)}</td>
                    <td className="py-2 font-mono text-xs text-zinc-500">{e.pin}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          </>
        )}
      </section>

      {isManager && <Enrolled />}
    </div>
  );
}
